<template>
    <div class="animated fadeIn">
        <b-row class="my-3">
            <b-col>
                <h4 class="w-100 text-center text-primary">
                    <i class="fa fa-industry text-primary mr-2"></i>
                    Listado de Remesas
                </h4>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-card>
                    <b-row slot="header" class="m-0 p-0 justify-content-between align-items-center">
                        Remesas
                        <div>
                            <button
                                :disabled="count<1"
                                class="btn btn-primary btn-sm mr-2"
                                @click="exportConsignments('csv')">
                                <i class="fa fa-download"></i> CSV
                            </button>
                            <button
                                :disabled="count<1"
                                class="btn btn-primary btn-sm"
                                @click="exportConsignments('xlsx')">
                                <i class="fa fa-download"></i> Excel
                            </button>
                            <button
                                :disabled="count<1"
                                class="btn btn-primary btn-sm ml-2"
                                @click="exportConsignments('pdf')">
                                <i class="fa fa-download"></i> PDF
                            </button>
                        </div>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col cols="10">
                            <b-row>
                                <b-col cols="6">
                                    <headquarters-multiple-selector
                                        id="headquarters"
                                        v-model="search.headquarters"
                                        fromUser></headquarters-multiple-selector>
                                </b-col>

                                <b-col cols="6">
                                    <month-and-year-selector
                                        v-model="search.issueDate"></month-and-year-selector>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col cols="3">
                                    <consignment-status-selector
                                        id="status"
                                        v-model="search.status"></consignment-status-selector>
                                </b-col>

                                <b-col cols="3">
                                    <consignment-type-selector
                                        id="type"
                                        v-model="search.type"></consignment-type-selector>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col cols="2">
                            <div class="d-flex flex-row-reverse">
                                <button
                                    class="btn btn-pill btn-primary px-4"
                                    @click="searchConsignments">
                                    Filtrar
                                </button>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <v-server-table
                                ref="ConsignmentsTable"
                                class="dataTable"
                                md="12"
                                lg="12"
                                sm="12"
                                :columns="columns"
                                :options="options"
                                :theme="theme"
                                @sorted="tableSorted">
                                <template slot="beforeBody">
                                    <tr v-if="loading">
                                        <td class="text-center loading" colspan="11"><spinner size="medium" line-fg-color="#025542" line-bg-color="#c8ced3"></spinner></td>
                                    </tr>
                                </template>
                                <template slot="select" slot-scope="props">
                                    <input type="checkbox" :value="props.row.uuid" v-model="selectedConsignmentsIds">
                                </template>
                                <template slot="uuid" slot-scope="props">
                                    <router-link
                                        v-b-tooltip.hover.top
                                        title="Ver"
                                        :to="{ name: 'consignmentOverview', params: { uuid: props.row.uuid  }}">
                                        <i class="fa fa-eye"></i>
                                    </router-link>
                                </template>

                                <template slot="numberReceipts" slot-scope="props">
                                    {{props.row.numberReceipts}}
                                </template>

                                <template slot="totalAmount" slot-scope="props">
                                    {{props.row.totalAmount}}€
                                </template>

                                <template slot="presentationDate" slot-scope="props">
                                    {{props.row.presentationDate | dateFormat('DD/MM/YYYY')}}
                                </template>

                                <template slot="chargeDate" slot-scope="props">
                                    {{props.row.chargeDate | dateFormat('DD/MM/YYYY')}}
                                </template>

                                <template slot="status" slot-scope="props">
                                    {{translateConsignmentStatus(props.row.status)}}
                                </template>

                                <template slot="headquarters" slot-scope="props">
                                    {{props.row.headquarters.code}}
                                </template>

                                <template slot="type" slot-scope="props">
                                    {{translateConsignmentType(props.row.type)}}
                                </template>

                                <template slot="afterFilter">
                                    <b-row>
                                        <b-col>
                                            <consignment-action-selector
                                                v-model="actionSelector"
                                                :allowEmpty="true"
                                                :searchData="searchData"
                                                :selectedConsignmentsIds="selectedConsignmentsIds"
                                                :allowConsignmentActions="allowConsignmentActions"
                                                :consignmentIssuePermission="!!mixinCheckPermission('CONSIGNMENT_ISSUE')"
                                                :consignmentPostPermission="!!mixinCheckPermission('CONSIGNMENT_POST')"
                                                :consignmentDeletePermission="!!mixinCheckPermission('CONSIGNMENT_DELETE')"
                                                @selected-action="selectedAction"></consignment-action-selector>
                                        </b-col>
                                        <b-col>
                                            <div v-if="!!selectedConsignmentsIds.length" class="selected-consignments"><i class="fa fa-check" aria-hidden="true"></i>Remesas seleccionadas: <span class="number">{{ selectedConsignmentsIds.length }}</span></div>
                                        </b-col>
                                    </b-row>
                                </template>
                            </v-server-table>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
    import Vue from 'vue';
    import {ServerTable, Event} from 'vue-tables-2';
    import form from '@/helpers/form';
    import {downloadJsonAsPdf} from '@/helpers/js-pdf';
    import {downloadJsonAsCsv, downloadJsonAsXlsx} from '@/helpers/js-xlsx';
    import {getAttachmentPreview} from '@/api/attachment-api';
    import {
        getConsignments,
        issueConsignments,
        postConsignments,
        deleteConsignments,
        getConsignmentSeatsFiles} from '@/api/consignment-api';
    import {getConsignmentStatusByKey, getConsignmentTypeByKey} from '@/helpers/consignment-helper';
    import consignmentTypeSelector from '@/components/consignments/ConsignmentTypeSelector';
    import consignmentStatusSelector from '@/components/consignments/ConsignmentStatusSelector';
    import consignmentActionSelector from '@/components/consignments/ConsignmentActionSelector';
    import monthAndYearSelector from '@/components/MonthAndYearSelector';
    import headquartersMultipleSelector from '@/components/headquarters/HeadquartersMultipleSelector';
    import Spinner from 'vue-simple-spinner';
    import JSZip from 'jszip'
    import { saveAs } from 'file-saver';
    import { v4 as uuidv4 } from 'uuid';

    Vue.use(ServerTable);

    export default {
        name: "ListConsignments",
        components: {
            consignmentTypeSelector,
            consignmentStatusSelector,
            consignmentActionSelector,
            monthAndYearSelector,
            headquartersMultipleSelector,
            Spinner
        },
        data() {
            return {
                topmenu: [
                    {
                        name: 'Crear Remesa',
                        path: '/accounting/consignments/add',
                        brand: 'fa fa-industry'
                    }
                ],
                loading: true,
                columns: ['select', 'uuid', 'code', 'presentationDate', 'chargeDate', 'description', 'numberReceipts', 'totalAmount', 'status', 'headquarters', 'type'],
                options: {
                    headings: {
                        select: {
                            text: ''
                        },
                        uuid: {
                            text: '',
                            id: 'fit'
                        },
                        code: 'Código',
                        presentationDate: 'F. Presentación',
                        chargeDate: 'F. Cargo',
                        description: 'Descripción',
                        numberReceipts: 'No. de recibos',
                        totalAmount: 'Importe total',
                        status: 'Estado',
                        headquarters: 'Sede',
                        type: 'Tipo'
                    },
                    columnsClasses: {
                        select: 'select',
                        uuid: "fit normal-query"
                    },
                    sortIcon: {
                        base: 'fa',
                        up: 'fa-sort-asc',
                        down: 'fa-sort-desc',
                        is: 'fa-sort'
                    },
                    perPageValues: [10, 25, 50, 100, 500, 1000],
                    sortable: ['code', 'presentationDate', 'chargeDate', 'description', 'numberReceipts', 'totalAmount', 'status', 'headquarters', 'type'],
                    filterable: false,
                    texts: null,
                    pagination: {
                        chunk: 10,
                        edge: true,
                        nav: 'scroll'
                    },
                    rowClassCallback: (row) => this.getRowClassByStatus(row),
                    requestFunction: (data) => {
                        if (this.$store.getters.getFilters[0] !== null) {
                          this.search.headquarters = this.$store.getters.getFilters[0];
                        }
                        if (this.$store.getters.getFilters[1] !== null) {
                          this.search.issueDate = this.$store.getters.getFilters[1];
                        }
                        if (this.$store.getters.getFilters[2] !== null) {
                          this.search.status = this.$store.getters.getFilters[2];
                        }
                        if (this.$store.getters.getFilters[3] !== null) {
                          this.search.type = this.$store.getters.getFilters[3];
                        }
                        this.search.page = data.page;
                        this.search.itemsPerPage = data.limit;
                        return getConsignments(this.searchData);
                    },
                    responseAdapter: (data) => {
                        return {
                            data: data.consignments,
                            count: data.count
                        }
                    },
                },
                theme: 'bootstrap4',
                search: {
                    page: 1,
                    itemsPerPage: 10,
                    issueDate: null,
                    headquarters: null,
                    status: null,
                    type: null
                },
                orderBy: {
                    column: null,
                    ascending: null
                },
                actionSelector : null,
                selectedConsignmentsIds: [],
                selectedConsignments: {},
                loadedConsignments: [],
                isAllConsignmentsSelected: false,
                allowConsignmentActions: [],
                count: 0
            }
        },
        created() {
            this.$_loadingMixin_showLoading();
        },
        mounted() {
            this.options.texts = form.tableLabels();
            this.$eventHub.$emit('captureMenu', this.topmenu);

            Event.$on('vue-tables.loading', (data) => {
                this.$_loadingMixin_showLoading();
                this.count = 0;
                this.loading = true;
                //this.$refs.ConsignmentsTable.data = [];
                if (!!document.querySelector('.VueTables__no-results')) {
                    document.querySelector('.VueTables__no-results').style.display = 'none';
                }
            });
            Event.$on('vue-tables.loaded', (data) => {
                if (!!data && !!data.count) {
                    this.count = data.count;
                    this.loadedConsignments = data.consignments;
                }
                this.$_loadingMixin_hideLoading();
                this.loading = false;
                if (!!document.querySelector('.VueTables__no-results')) {
                    document.querySelector('.VueTables__no-results').style.display = 'table-row';
                }
            });
        },
        computed: {
            searchData() {
                let data = {
                    page: this.search.page,
                    itemsPerPage: this.search.itemsPerPage,
                };

                if (!!this.search.issueDate) {
                    data['issueDate'] = this.search.issueDate.toISOString();
                    this.deselectAllConsignments();
                }
                if (!!this.search.headquarters) {
                    data['headquartersIds'] = this.search.headquarters.map(headquarter => (headquarter.uuid));
                    this.deselectAllConsignments();
                }
                if (!!this.search.status) {
                    data['status'] = this.search.status;
                    this.deselectAllConsignments();
                }
                if (!!this.search.type) {
                    data['type'] = this.search.type;
                    this.deselectAllConsignments();
                }

                if (!!this.orderBy.column) {
                    data['orderColumn'] = this.orderBy.column;
                }
                data['orderAscending'] = this.orderBy.ascending ? 1 : 0;

                return data;
            },

            consignments() {
                return getConsignments(this.searchData)
            }
        },
        methods: {
            translateConsignmentType(typeKey) {
                return getConsignmentTypeByKey(typeKey);
            },
            translateConsignmentStatus(typeKey) {
                return getConsignmentStatusByKey(typeKey);
            },
            getRowClassByStatus: function (row) {
                let classString = "consignment-row ";
                switch (row.status) {
                    case 'ISSUED':
                        classString += 'orange-consignment';
                        break;
                    case 'POSTED':
                        classString += 'green-consignment';
                        break;
                    default:
                        classString += 'normal-consignment';
                }
                return classString;
            },
            searchConsignments() {
                let filterData = [];
                filterData.push(this.search.headquarters);
                filterData.push(this.search.issueDate);
                filterData.push(this.search.status);
                filterData.push(this.search.type);
                this.$store.commit('SET_FILTERS_LIST', filterData);
                this.$refs.ConsignmentsTable.refresh();
            },
            async exportConsignments(format) {
                if (this.count > 0) {
                    this.$_loadingMixin_showLoading();

                    // let parameters = Object.assign({}, this.searchData);
                    // parameters.page = 1;
                    // parameters.itemsPerPage = this.count;
                    //
                    // let response = await getConsignments(parameters);
                    let response = await getConsignments(this.searchData);
                    let rows = response.consignments.map((consignment) => {
                        return this.normalizeConsignment(consignment);
                    });

                    if (format === 'csv') {
                        downloadJsonAsCsv(rows);
                    } else if (format === 'xlsx') {
                        downloadJsonAsXlsx(rows);
                    } else if (format === 'pdf') {
                        downloadJsonAsPdf(rows);
                    }

                    this.$_loadingMixin_hideLoading();
                }
            },
            normalizeConsignment(consignment) {
                return {
                    'Código': consignment.code,
                    'F. Presentación': consignment.presentationDate.toLocaleDateString(),
                    'F. Cargo': consignment.chargeDate.toLocaleDateString(),
                    'Descripción': consignment.description,
                    'Estado': this.translateConsignmentStatus(consignment.status),
                    'Sede': consignment.headquarters.code,
                    'Tipo': this.translateConsignmentType(consignment.type)
                };
            },
            tableSorted(data) {
                this.orderBy.column = data.column;
                this.orderBy.ascending = data.ascending;
            },
            selectedAction(action) {
                switch (action) {
                    case 'Seleccionar remesas visibles':
                        this.selectVisibleConsignments();
                        break;
                    case 'Deseleccionar todas':
                        this.deselectAllConsignments();
                        break;
                    case 'Remesar':
                        this.issueConsignments(this.selectedConsignmentsIds);
                        break;
                    case 'Contabilizar':
                        this.postConsignments(this.selectedConsignmentsIds);
                        break;
                    case 'Eliminar':
                        this.deleteConsignments(this.selectedConsignmentsIds);
                        break;
                    case 'Descargar SEPAs':
                        this.previewSepasXml(this.selectedConsignmentsIds);
                        break;
                    case 'Descargar Asientos':
                        this.getSeatsFiles(this.selectedConsignmentsIds);
                        break;
                }
            },
            selectVisibleConsignments() {
                this.selectedConsignmentsIds = []
                if (!this.isAllConsignmentsSelected) {
                    for (let i in this.loadedConsignments) {
                        this.selectedConsignmentsIds.push(this.loadedConsignments[i].uuid);
                    }
                }
            },
            deselectAllConsignments() {
                this.selectedConsignmentsIds = [];
            },
            issueConsignments(uuids) {
                let partialTitle = (uuids.length == 1) ? 'la remesa seleccionada' : uuids.length + ' remesas';
                let successfulMessage = (uuids.length == 1) ? 'La remesa ha sido remesada' : uuids.length + ' remesas han sido remesadas';
                let errorMessage = (uuids.length == 1) ? 'No se ha podido remesar la remesa' : 'No se han podido remesar las remesas';
                document.activeElement.blur();
                swal({
                    title: '¿Desea remesar ' + partialTitle + '?',
                    type: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Cancelar',
                    confirmButtonText: 'Confirmar',
                    buttonsStyling: false,
                    confirmButtonClass: 'btn btn-brand btn-primary btn-lg',
                    cancelButtonClass: 'btn btn-brand btn-light btn-md mr-4',
                    reverseButtons: true
                }).then((response) => {
                    if (!!response.value) {
                        this.$_loadingMixin_showLoading();
                        issueConsignments(uuids)
                            .then(() => {
                                this.$refs.ConsignmentsTable.getData();
                                this.deselectAllConsignments();
                                this.$_notificationMixin_success('Operación completada', successfulMessage);
                                this.$_loadingMixin_hideLoading();
                            })
                            .catch(() => {
                                this.$_notificationMixin_error('Error en la operación', errorMessage);
                                this.$_loadingMixin_hideLoading();
                            });
                    }
                });
            },
            postConsignments(uuids) {
                let partialTitle = (uuids.length == 1) ? 'la remesa seleccionada' : uuids.length + ' remesas';
                let successfulMessage = (uuids.length == 1) ? 'La remesa ha sido contabilizada' : uuids.length + ' remesas han sido contabilizadas';
                let errorMessage = (uuids.length == 1) ? 'No se ha podido contabilizar la remesa' : 'No se han podido contabilizar las remesas';
                document.activeElement.blur();
                swal({
                    title: '¿Desea contabilizar ' + partialTitle + '?',
                    type: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Cancelar',
                    confirmButtonText: 'Confirmar',
                    buttonsStyling: false,
                    confirmButtonClass: 'btn btn-brand btn-primary btn-lg',
                    cancelButtonClass: 'btn btn-brand btn-light btn-md mr-4',
                    reverseButtons: true
                }).then((response) => {
                    if (!!response.value) {
                        this.$_loadingMixin_showLoading();
                        postConsignments(uuids)
                            .then(() => {
                                this.$refs.ConsignmentsTable.getData();
                                this.deselectAllConsignments();
                                this.$_notificationMixin_success('Operación completada', successfulMessage);
                                this.$_loadingMixin_hideLoading();
                            })
                            .catch(() => {
                                this.$_notificationMixin_error('Error en la operación', errorMessage);
                                this.$_loadingMixin_hideLoading();
                            });
                    }
                });
            },
            deleteConsignments(uuids) {
                let partialTitle = (uuids.length == 1) ? 'la remesa seleccionada' : uuids.length + ' remesas';
                let successfulMessage = (uuids.length == 1) ? 'La remesa ha sido eliminada' : uuids.length + ' remesas han sido eliminadas';
                let errorMessage = (uuids.length == 1) ? 'No se ha podido eliminar la remesa' : 'No se han podido eliminar las remesas';
                document.activeElement.blur();
                swal({
                    title: '¿Desea eliminar ' + partialTitle + '?',
                    text: "Esta operación no se podrá revertir",
                    type: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Cancelar',
                    confirmButtonText: 'Confirmar',
                    buttonsStyling: false,
                    confirmButtonClass: 'btn btn-brand btn-primary btn-lg',
                    cancelButtonClass: 'btn btn-brand btn-light btn-md mr-4',
                    reverseButtons: true
                }).then((response) => {
                    if (!!response.value) {
                        this.$_loadingMixin_showLoading();
                        deleteConsignments(uuids)
                            .then(() => {
                                this.$refs.ConsignmentsTable.getData();
                                this.deselectAllConsignments();
                                this.$_notificationMixin_success('Operación completada', successfulMessage);
                                this.$_loadingMixin_hideLoading();
                            })
                            .catch(() => {
                                this.$_notificationMixin_error('Error en la operación', errorMessage);
                                this.$_loadingMixin_hideLoading();
                            });
                    }
                });
            },
            previewSepasXml(uuids) {
                this.$_loadingMixin_showLoading();

                for (let i in uuids) {
                    let consignment = this.getConsignmentFromArray(uuids[i]);
                    if (!!consignment.sepaXml) {
                        this.previewSepaXml(consignment);
                    }
                }

                this.deselectAllConsignments();
                this.$_loadingMixin_hideLoading();
            },
            async previewSepaXml(consignment) {
                this.$_loadingMixin_showLoading();
                await getAttachmentPreview(consignment.sepaXml, {type: 'application/xml'})
                    .then((dataStream) => {
                        const file = new Blob([dataStream]);
                        const fileURL = URL.createObjectURL(file);
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(file);
                        link.download = consignment.code + '-sepa-direct-debit.xml';
                        link.click();
                        URL.revokeObjectURL(link.href);
                    })
                    .catch(() => {
                        this.$_notificationMixin_error("Se ha producido un error", "No hemos podido recuperar la previsualización del documento");
                    });
                this.$_loadingMixin_hideLoading();
            },
            async getSeatsFiles(uuids) {
                this.$_loadingMixin_showLoading();
                const zipFile = new JSZip();

                for await (let uuid of uuids) {
                  let consignment = this.getConsignmentFromArray(uuid);
                  const zipFileTmp = new JSZip();
                  if (!!consignment.sepaXml) {
                    const fileArray = await this.getSeatsFile(consignment);
                    const file = new Blob([fileArray], {type: 'application/zip'});
                    const zip = await zipFileTmp.loadAsync(file)
                    for (let f of Object.keys(zip.files)) {
                      const zfile = await zip.file(zip.files[f].name).async('string')
                      zipFile.file(`${zip.files[f].name}`, zfile )
                    }
                  }
                }
                const blob = await zipFile.generateAsync({type: 'blob'})
                saveAs(blob, `${uuidv4()}.zip`)
                this.deselectAllConsignments();
                this.$_loadingMixin_hideLoading();
            },
            async getSeatsFile(consignment) {
                try {
                    this.$_loadingMixin_showLoading();
                    const dataStream = await getConsignmentSeatsFiles(consignment.uuid)
                    this.$_loadingMixin_hideLoading();
                    return dataStream;
                } catch (error) {
                    this.$_loadingMixin_hideLoading();
                    console.log(e)
                    this.$_notificationMixin_error("Se ha producido un error", "No hemos podido recuperar los asientos");
                }

            },
            getConsignmentFromArray(uuid) {
                let index = this.loadedConsignments.findIndex(item => item.uuid == uuid);
                return this.loadedConsignments[index];
            }
        },
        watch: {
            selectedConsignmentsIds: function() {
                this.allowConsignmentActions['issue'] = true;
                this.allowConsignmentActions['post'] = true;
                this.allowConsignmentActions['delete'] = true;
                this.allowConsignmentActions['download_sepa'] = true;
                this.allowConsignmentActions['download_seats'] = true;

                for (let i in this.selectedConsignmentsIds) {
                    let consignment = this.getConsignmentFromArray(this.selectedConsignmentsIds[i]);

                    switch (consignment.status) {
                        case 'CREATED':
                            this.allowConsignmentActions['post'] = false;
                            this.allowConsignmentActions['download_sepa'] = false;
                            this.allowConsignmentActions['download_seats'] = false;
                            break;
                        case 'ISSUED':
                            this.allowConsignmentActions['download_seats'] = false;
                            break;
                        case 'POSTED':
                            this.allowConsignmentActions['issue'] = false;
                            this.allowConsignmentActions['post'] = false;
                            this.allowConsignmentActions['delete'] = false;
                            if (!consignment.seatsDiary || !consignment.seatsSubAccounts) {
                                this.allowConsignmentActions['download_seats'] = false;
                            }
                            break;
                    }
                }
            },
            loadedConsignments: function() {
                this.deselectAllConsignments();
            }

        }
    }
</script>

<style lang="scss">
    .orange-consignment {
        background-color: darkorange !important;
    }

    .green-consignment {
        background-color: lightgreen !important;
    }

    .normal-consignment {
        background-color: white !important;
    }

    .VueTables__no-results {
        display: none;
    }

    .selected-consignments {
        padding: 7px;
        background-color: #41b883;
        color: #ffffff;
        border-radius: 5px;
    }

    .selected-consignments i {
        margin-right: 7px;
    }

    .selected-consignments .number {
        padding-left: 5px;
        font-weight: bold;
    }

    .VueTables .VueTables__search {
        width: calc(66.6% + 20px);
    }

    .VueTables .VueTables__search .row {
        width: 100%;
    }
</style>
